import { useEffect, useState, useRef, useCallback } from 'react';
import { AxiosResponse } from 'axios';
import { connect } from 'react-redux';
import { mapDispatchProps, mapStateToProps } from '../../store/store';

// Core
import API from '../../api';
import { getLanguage, useLanguage } from '@languages/index';

// Components
import ModelCard, { TYPES } from 'components/modelCard';
import LoadingText from 'components/loadingText';
import BottomGradient from 'components/bottomGradient';
import ModalRequestRecruitingCampaignApplicationBrand from 'components/modalBrandRequestRecruitingCampaignApplication';
import Checkbox from 'components/checkbox';
import RadioButton from 'components/radioButton';

export interface InfluencersShowProps {
  auth: any;
  brand: any;
  preferences: any;
  showModal: (config: any) => void;
  closeModal?: any;
  closeOverlays: () => void;
}

// ============================================
// Custom Hook: Manage all influencer sections
// ============================================

const useInfluencerSections = () => {
  const [sections, setSections] = useState<{
    [key: string]: any[];
  }>({
    brandLiked: undefined,  
    // previouslySelected: undefined,  
  });

  // Update influencer brand-level like across ALL sections at once
  const updateInfluencerBrandLikeInAllSections = useCallback(
    (userId: number, newLikeStatus: any) => {
      setSections((prev) => {
        const updated: { [key: string]: any[] } = {};

        for (const sectionKey in prev) {
          if (prev[sectionKey]) {
            updated[sectionKey] = prev[sectionKey].map((item: any) =>
              item.userId === userId
                ? { ...item, likes: newLikeStatus }
                : item
            );
          } else {
            updated[sectionKey] = prev[sectionKey];
          }
        }

        return updated;
      });
    },
    []
  );

  // Update influencer product-level likes in ALL sections
  // const updateInfluencerProductLikeInAllSections = useCallback(
  //   (userId: number, newProductLikes: any) => {
  //     setSections((prev) => {
  //       const updated: { [key: string]: any[] } = {};

  //       for (const sectionKey in prev) {
  //         if (prev[sectionKey]) {
  //           updated[sectionKey] = prev[sectionKey].map((item: any) =>
  //             item.userId === userId
  //               ? { ...item, productUserLikes: newProductLikes }
  //               : item
  //           );
  //         } else {
  //           updated[sectionKey] = prev[sectionKey];
  //         }
  //       }

  //       return updated;
  //     });
  //   },
  //   []
  // );

  return {
    sections,
    setSections,
    updateInfluencerBrandLikeInAllSections,
    // updateInfluencerProductLikeInAllSections,
  };
};

// ============================================
// Main Component
// ============================================

const InfluencersShow: React.FC<InfluencersShowProps> = (props) => {
  const language = useLanguage();
  const languageTexts = {
    ...language.common,
    ...language.pages.campaigns,
    ...language.pages.influencers,
  };

  // ============================================
  // State - Influencer Sections Management
  // ============================================

  const {
    sections,
    setSections,
    updateInfluencerBrandLikeInAllSections,
    // updateInfluencerProductLikeInAllSections,
  } = useInfluencerSections();

  const brandLikedInfluencers = sections.brandLiked;
  // const previouslySelectedInfluencers = sections.previouslySelected;  

  const [isBrandLikedFetching, setIsBrandLikedFetching] = useState(false); 
  const [isBrandLikedEnded, setIsBrandLikedEnded] = useState(false); 

  const brandLikedPaginator = useRef<any>(null); 
  const currentBrandLikedPage = useRef(1); 

  const [filterBrandLikedIsSuggested, setFilterBrandLikedIsSuggested] = useState(false);
  const [filterBrandLikedIsNewInfluencer, setFilterBrandLikedIsNewInfluencer] = useState(false);
  const sortBrandLikedOptions = [
  { name: languageTexts?.show_text_sort_by_brand_liked_influencers_date, value: "created_at" },
  { name: languageTexts?.show_text_sort_by_brand_liked_influencers_followers, value: "followers" },
  { name: languageTexts?.show_text_sort_by_brand_liked_influencers_previously_selected, value: "selection_count" },
  ];
  const [selectedSortBrandLikedOption, setSelectedSortBrandLikedOption] = useState(sortBrandLikedOptions[0]);
  const [sortBrandLikedBy, setSortBrandLikedBy] = useState('created_at'); // 'created_at', 'followers', 'selection_count'

  // Previously selected state  
  // const [isPreviouslySelectedFetching, setIsPreviouslySelectedFetching] = useState(false);
  // const [isPreviouslySelectedEnded, setIsPreviouslySelectedEnded] = useState(false);
  // const previouslySelectedPaginator = useRef<any>(null);
  // const currentPreviouslySelectedPage = useRef(1);

  // ============================================
  // API Functions
  // ============================================

  // Brand-level like - UPDATE ALL SECTIONS
  const handleBrandLike = useCallback(
    async (influencer: any) => {
      try {
        const response = await API.Like.store({
          brandToken: props.auth.token,
          subject: 'Users',
          subjectId: influencer.userId,
        });

        const newLikeStatus = response.data.result ? [true] : [];

        // Update across ALL sections
        updateInfluencerBrandLikeInAllSections(influencer.userId, newLikeStatus);
      } catch (error: any) {
        props.showModal({
          text: error?.response?.data?.message || error.message,
        });
      }
    },
    [props, updateInfluencerBrandLikeInAllSections]
  );

  // Product-level like - UPDATE ALL SECTIONS
  // const handleProductUserLike = useCallback(
  //   async (
  //     productId: number,
  //     userId: number,
  //     currentLikeId: number | null,
  //     brandToken: string
  //   ) => {
  //     try {
  //       let response;

  //       if (currentLikeId) {
  //         // Delete product like
  //         response = await API.ProductUserLike.delete(currentLikeId, brandToken);
  //       } else {
  //         // Create product like
  //         response = await API.ProductUserLike.store({
  //           brandtoken: brandToken,
  //           productid: productId,
  //           userid: userId,
  //         });
  //       }

  //       const newProductLikes = currentLikeId
  //         ? []
  //         : [response?.data?.data];

  //       // Update across ALL sections
  //       updateInfluencerProductLikeInAllSections(userId, newProductLikes);
  //     } catch (error: any) {
  //       console.error(
  //         'Product user like error:',
  //         error?.response?.data?.message || error?.message
  //       );
  //     }
  //   },
  //   [updateInfluencerProductLikeInAllSections]
  // );

  // ============================================
  // Section 1: Fetch brand-liked influencers
  // ============================================

const fetchBrandLikedInfluencers = useCallback( 
  async (config?: any, sortByOverride?: string, isSuggestedOverride?: boolean, isNewInfluencerOverride?: boolean) => {
      let targetPage = currentBrandLikedPage.current;

      if (config?.refresh === true) {
        targetPage = 1;
        currentBrandLikedPage.current = 1;
        setIsBrandLikedEnded(false);

        setSections((prev) => ({
        ...prev,
        brandLiked: undefined,  // Shows loading state
      }));
      }

      // let params: any = {
      //   brandToken: props.auth.token,
      //   orderColumn: 'connected_at',
      //   orderValue: 'desc',
      //   page: targetPage,
      //   size: 50,
      // };
      let params: any = {
        brandToken: props.auth.token,
        orderColumn: sortByOverride || sortBrandLikedBy,
        orderValue: 'desc',
        page: targetPage,
        size: 50,
        isSuggested: (isSuggestedOverride !== undefined ? isSuggestedOverride : filterBrandLikedIsSuggested) || undefined,
        isNewInfluencer: (isNewInfluencerOverride !== undefined ? isNewInfluencerOverride : filterBrandLikedIsNewInfluencer) || undefined,
      };

      setIsBrandLikedFetching(true);

      try {
        const response: AxiosResponse = await API.Like.brandLikedInfluencers(params);
        const paginator = response.data.paginator;

        setSections((prev) => ({
          ...prev,
          brandLiked: 
            targetPage === 1
              ? response.data.influencers
              : [
                  ...(prev.brandLiked || []),
                  ...response.data.influencers,
                ],
        }));

        brandLikedPaginator.current = paginator;

        if (paginator?.current_page === paginator?.last_page) {
          setIsBrandLikedEnded(true);
        }

        currentBrandLikedPage.current = targetPage + 1;
        setIsBrandLikedFetching(false);
      } catch (error: any) {
        props.showModal({
          text:
            error?.response?.data?.message ||
            error.message ||
            'Error fetching brand-liked influencers',
        });
        setIsBrandLikedFetching(false);
      }
    },
    [props, setSections, sortBrandLikedBy, filterBrandLikedIsSuggested, filterBrandLikedIsNewInfluencer]
  );

  // ============================================
  // Section 2: Fetch previously selected influencers - COMMENTED OUT
  // ============================================

  /*
  const fetchPreviouslySelectedInfluencers = useCallback(
    async (config?: any) => {
      let targetPage = currentPreviouslySelectedPage.current;

      if (config?.refresh === true) {
        targetPage = 1;
        currentPreviouslySelectedPage.current = 1;
        setIsPreviouslySelectedEnded(false);
      }

      let params: any = {
        brandToken: props.auth.token,
        isPreviouslySelected: true,
        orderColumn: 'connected_at',
        orderValue: 'desc',
        page: targetPage,
        size: 50,
      };

      setIsPreviouslySelectedFetching(true);

      try {
        const response: AxiosResponse = await API.Like.likedInfluencers(params);
        const paginator = response.data.paginator;

        setSections((prev) => ({
          ...prev,
          previouslySelected:
            targetPage === 1
              ? response.data.influencers
              : [
                  ...(prev.previouslySelected || []),
                  ...response.data.influencers,
                ],
        }));

        previouslySelectedPaginator.current = paginator;

        if (paginator?.current_page === paginator?.last_page) {
          setIsPreviouslySelectedEnded(true);
        }

        currentPreviouslySelectedPage.current = targetPage + 1;
        setIsPreviouslySelectedFetching(false);
      } catch (error: any) {
        props.showModal({
          text:
            error?.response?.data?.message ||
            error.message ||
            'Error fetching previously selected influencers',
        });
        setIsPreviouslySelectedFetching(false);
      }
    },
    [props, setSections]
  );
  */

  // ============================================
  // Effects
  // ============================================

  // Initial data load on mount - FIX: Empty dependency array to prevent multiple calls
  useEffect(() => {
    fetchBrandLikedInfluencers({ refresh: true }); 
    // fetchPreviouslySelectedInfluencers({ refresh: true });  
  }, []); 

  // Manual Scroll Handler for Infinite Scroll
  useEffect(() => {
    function handleScroll() {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollThreshold = 0.8;

      if (
        scrollY + windowHeight >= documentHeight * scrollThreshold &&
        !isBrandLikedFetching &&
        !isBrandLikedEnded
      ) {
        fetchBrandLikedInfluencers();
      }

      // Previously selected scroll handler  
      /*
      if (
        scrollY + windowHeight >= documentHeight * scrollThreshold &&
        !isPreviouslySelectedFetching &&
        !isPreviouslySelectedEnded
      ) {
        fetchPreviouslySelectedInfluencers();
      }
      */
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [
    isBrandLikedFetching,
    isBrandLikedEnded,
    fetchBrandLikedInfluencers,
    // isPreviouslySelectedFetching,  
    // isPreviouslySelectedEnded,  
    // fetchPreviouslySelectedInfluencers,  
  ]);

  // ============================================
  // Render Functions
  // ============================================

  const renderBrandLikedInfluencers = () => { 
    if (brandLikedInfluencers === undefined) {
      return (
        <div className="flex flex-row justify-center text-center my-80px">
          <LoadingText
            text={languageTexts?.loading_1}
            render={(text) => (
              <span className="prose prose-xl text-slate-400">{text}</span>
            )}
          />
        </div>
      );
    }

    if (!brandLikedInfluencers || brandLikedInfluencers.length === 0) {
      return (
        <div className="flex flex-col gap-1 justify-center text-center my-80px">
          <span className="font-bold text-sm tracking-tighter text-gray-400">
            {languageTexts?.no_influencers_found}
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-col mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 md:my-6 gap-y-8 gap-x-4">
          {brandLikedInfluencers.map((influencer: any, index: number) => (
            <ModelCard
              type={TYPES.CAMPAIGN}
              key={`brand-liked-model-card-${influencer.id}-${index}`}  
              application={influencer}
              onLike={async () => {
                await handleBrandLike(influencer);
              }}
              // onProductUserLike={async () => {
              //   await handleProductUserLike(
              //     influencer.id,
              //     influencer.id,
              //     influencer.productUserLikes?.[0]?.id || null,
              //     props.auth.token
              //   );
              // }}
              isInfluencersPage={true}
            />
          ))}
        </div>

        {isBrandLikedFetching && (
          <div className="flex justify-center py-8">
            <LoadingText
              text={languageTexts?.loading_1}
              render={(text) => (
                <span className="text-slate-400">{text}</span>
              )}
            />
          </div>
        )}
      </div>
    );
  };

  // Previously selected render function - COMMENTED OUT
  /*
  const renderPreviouslySelectedInfluencers = () => {
    if (previouslySelectedInfluencers === undefined) {
      return (
        <div className="flex flex-row justify-center text-center my-80px">
          <LoadingText
            text={languageTexts?.loading_1}
            render={(text) => (
              <span className="prose prose-xl text-slate-400">{text}</span>
            )}
          />
        </div>
      );
    }

    if (
      !previouslySelectedInfluencers ||
      previouslySelectedInfluencers.length === 0
    ) {
      return (
        <div className="flex flex-col gap-1 justify-center text-center my-80px">
          <span className="font-bold text-sm tracking-tighter text-gray-400">
            {languageTexts.showtext37}
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-col mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 md:my-6 gap-y-8 gap-x-4">
          {previouslySelectedInfluencers.map((influencer: any, index: number) => (
            <ModelCard
              type={TYPES.CAMPAIGN}
              key={`previously-selected-model-card-${influencer.id}-${index}`}
              application={influencer}
              onLike={async () => {
                await handleBrandLike(influencer);
              }}
              onProductUserLike={async () => {
                await handleProductUserLike(
                  influencer.id,
                  influencer.id,
                  influencer.productUserLikes?.[0]?.id || null,
                  props.auth.token
                );
              }}              
            />
          ))}
        </div>

        {isPreviouslySelectedFetching && (
          <div className="flex justify-center py-8">
            <LoadingText
              text={languageTexts?.loading_1}
              render={(text) => (
                <span className="text-slate-400">{text}</span>
              )}
            />
          </div>
        )}
      </div>
    );
  };
  */

  // ============================================
  // Main Render
  // ============================================

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-5 md:mx-auto md:w-[800px] lg:w-[1024px] py-5">
        <h1 className="text-2xl font-bold tracking-tighter">
        </h1>
      </div>

      {/* Section 1: Previously Selected Influencers */}
      {/* <div className="px-5 md:mx-auto md:w-[800px] lg:w-[1024px] py-5 border-E8E8E8">
        <div className="flex flex-col gap-2">
          <div className="font-bold text-2xl tracking-tighter">
            {languageTexts?.previously_selected_influencer_title}
          </div>
          <div className="text-base tracking-tighter text-gray-600 whitespace-pre-line">
            {languageTexts?.previously_selected_influencer_desc}
          </div>
        </div>
        {renderPreviouslySelectedInfluencers()}
      </div> */}

      {/* Section 2: Brand Liked Influencers */}
      <div className="px-5 md:mx-auto md:w-[800px] lg:w-[1024px] py-5 border-E8E8E8 pb-[100px]">
        <div className="flex flex-col gap-4 mb-6">
        <div className="flex gap-4">
            <Checkbox
              text={languageTexts?.show_text_recommended_influencers}
              checked={filterBrandLikedIsSuggested}
              onClick={(checked) => {
                setFilterBrandLikedIsSuggested(checked);
                fetchBrandLikedInfluencers({ refresh: true }, undefined, checked, undefined);
              }}
            />
            <Checkbox
              text={languageTexts?.show_text_new_influencers}
              checked={filterBrandLikedIsNewInfluencer}
              onClick={(checked) => {
                setFilterBrandLikedIsNewInfluencer(checked);
                fetchBrandLikedInfluencers({ refresh: true }, undefined, undefined, checked);
              }}
            />
          </div>
          {/* <div className="flex gap-4"> */}
            <RadioButton
              className="gap-x-[25px] gap-y-[8px]"
              style={{ 
                flex: '0 1 auto',
                flexFlow: 'wrap',
                justifyContent: 'flex-start', 
                alignItems: 'flex-start' 
              }}
              buttonContainerStyle={{ width: 'auto' }}
              dots={true}
              borderless={true}
              singleLine={true}
              options={sortBrandLikedOptions}
              selected={selectedSortBrandLikedOption}
              onSelect={(option) => {
                setSelectedSortBrandLikedOption(option);
                setSortBrandLikedBy(option.value);
                fetchBrandLikedInfluencers({ refresh: true }, option.value);
              }}
            />
          {/* </div> */}
        </div>
        <div className="flex flex-col gap-2 mb-6">
          <div className="font-bold text-2xl tracking-tighter">
            {languageTexts?.brand_liked_influencer_title} ({brandLikedInfluencers?.length || 0})  
          </div>
          <div className="text-base tracking-tighter text-gray-600 whitespace-pre-line">
            {languageTexts?.brand_liked_influencer_desc}
          </div>
        </div>
        {renderBrandLikedInfluencers()} 
      </div>

      <BottomGradient />
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchProps)(InfluencersShow);
